const API_BASE = import.meta.env.VITE_API_URL || "/api/v1";

let _onUnauthorized: (() => void) | null = null;
export function onUnauthorized(cb: () => void) {
  _onUnauthorized = cb;
}
export function showLoginModal() {
  _onUnauthorized?.();
}

// HTTP хариуны алдааг network error-оос ялгахад ашиглана.
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type RequestExtras = { silent?: boolean };

async function request<T>(
  endpoint: string,
  options?: RequestInit & RequestExtras,
): Promise<T> {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const { silent, ...fetchOptions } = options || {};

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers: { ...headers, ...fetchOptions?.headers },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // 401 → зөвхөн token илгээсэн байсан, эндпойнт нь auth-ын биш,
    // мөн silent биш үед л modal-ыг гаргана.
    if (
      res.status === 401 &&
      !endpoint.startsWith("/auth/") &&
      token &&
      !silent
    ) {
      _onUnauthorized?.();
    }
    throw new ApiError(res.status, body.message || `API error ${res.status}`);
  }

  return res.json();
}

// ── Auth ──────────────────────────────────────────
export const api = {
  auth: {
    register: (data: { email: string; password: string; fullName: string }) =>
      request<{ message: string }>("/auth/register", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    verifyOtp: (data: { email: string; code: string }) =>
      request<{ token: string; user: any }>("/auth/verify-otp", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    login: (data: { email: string; password: string }) =>
      request<{ token: string; user: any }>("/auth/login", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    forgotPassword: (email: string) =>
      request<{ message: string }>("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    sendVerifyEmailCode: (email: string) =>
      request<{ message: string }>('/auth/verify-email/send', {
        method: 'POST', body: JSON.stringify({ email }),
      }),
    resetPassword: (data: {
      email: string;
      code: string;
      newPassword: string;
    }) =>
      request<{ message: string }>("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    facebook: (accessToken: string) =>
      request<{ token: string; user: any }>("/auth/facebook", {
        method: "POST",
        body: JSON.stringify({ accessToken }),
      }),
    google: (credential: string, mode: "login" | "register" = "login") =>
      request<
        { token: string; user: any } | { requireOtp: true; email: string }
      >("/auth/google", {
        method: "POST",
        body: JSON.stringify({ credential, mode }),
      }),
  },

  // ── Users ──────────────────────────────────────
  users: {
    getMe: (opts?: { silent?: boolean }) =>
      request<any>("/users/me", { silent: opts?.silent }),
    updateMe: (data: any) =>
      request<any>("/users/me", { method: "PUT", body: JSON.stringify(data) }),
    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      request<any>("/users/me/password", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    getMyCourses: () => request<any[]>("/users/me/courses"),
    getMyBookmarks: () => request<any[]>("/users/me/bookmarks"),
    getMyPayments: () => request<any[]>("/users/me/payments"),
    getMySubscription: () => request<any>("/users/me/subscription"),
    getPublicProfile: (id: string) => request<any>(`/users/${id}`),
    getUserPosts: (id: string) => request<any[]>(`/users/${id}/posts`),
    toggleFollow: (id: string) =>
      request<any>(`/users/${id}/follow`, { method: "POST" }),
    getSessions: () => 
      request<{ sessions: any[]; currentSessionId: string }>("/users/me/sessions"),
    revokeSessions: (sessionId: string) =>
      request<{ success: boolean }>(`/users/me/sessions/${sessionId}`, { method: "DELETE"}),
  },

  // ── Home ───────────────────────────────────────
  home: {
    get: () =>
      request<{
        featuredTeachers: any[];
        featuredCourses: any[];
        featuredVideos: any[];
        teachers: any[];
        heroSlides: any[];
        hero: { badge: string; title: string; subtitle: string };
        featuredSection: { title: string; subtitle: string };
        coursesSection: { title: string; subtitle: string };
        videosSection: { title: string; subtitle: string };
        teachersSection: { title: string; subtitle: string };
        premiumCta: { title: string; subtitle: string; buttonText: string };
        bottomCta: { title: string; subtitle: string; buttonText: string };
        faqs: { question: string; answer: string }[];
      }>("/home"),
  },

  // ── Courses ────────────────────────────────────
  courses: {
    list: (params?: {
      page?: number;
      limit?: number;
      category?: string;
      search?: string;
      priceType?: string;
      isMemberOnly?: string;
    }) => {
      const q = new URLSearchParams();
      if (params?.page) q.set("page", String(params.page));
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.category && params.category !== "Бүгд")
        q.set("category", params.category);
      if (params?.search) q.set("search", params.search);
      if (params?.priceType) q.set("priceType", params.priceType);
      if (params?.isMemberOnly) q.set("isMemberOnly", params.isMemberOnly);
      return request<{ data: any[]; pagination: any }>(`/courses?${q}`);
    },
    featured: () => request<any[]>("/courses/featured"),
    getById: (id: string) => request<any>(`/courses/${id}`),
    getLessons: (id: string) => request<any[]>(`/courses/${id}/lessons`),
    getReviews: (id: string, page = 1) =>
      request<{ data: any[]; pagination: any }>(
        `/courses/${id}/reviews?page=${page}`,
      ),
    getEnrollment: (id: string) => request<any>(`/courses/${id}/enrollment`),
    enroll: (id: string) =>
      request<any>(`/courses/${id}/enroll`, { method: "POST" }),
    addReview: (id: string, data: { rating: number; comment: string }) =>
      request<any>(`/courses/${id}/reviews`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    updateProgress: (id: string, lessonId: string) =>
      request<any>(`/courses/${id}/progress`, {
        method: "PUT",
        body: JSON.stringify({ lessonId }),
      }),
  },

  // ── Videos ─────────────────────────────────────
  videos: {
    list: (params?: {
      page?: number;
      limit?: number;
      category?: string;
      search?: string;
    }) => {
      const q = new URLSearchParams();
      if (params?.page) q.set("page", String(params.page));
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.category && params.category !== "Бүгд")
        q.set("category", params.category);
      if (params?.search) q.set("search", params.search);
      return request<{ data: any[]; pagination: any }>(`/videos?${q}`);
    },
    featured: () => request<any[]>("/videos/featured"),
    getById: (id: string) => request<any>(`/videos/${id}`),
    getRelated: (id: string) => request<any[]>(`/videos/${id}/related`),
    incrementView: (id: string) =>
      request<any>(`/videos/${id}/view`, { method: "POST" }),
  },

  // ── Teachers ───────────────────────────────────
  teachers: {
    list: (params?: {
      page?: number;
      limit?: number;
      specialty?: string;
      search?: string;
    }) => {
      const q = new URLSearchParams();
      if (params?.page) q.set("page", String(params.page));
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.specialty && params.specialty !== "Бүгд")
        q.set("specialty", params.specialty);
      if (params?.search) q.set("search", params.search);
      return request<{ data: any[]; pagination: any }>(`/teachers?${q}`);
    },
    featured: () => request<any>("/teachers/featured"),
    getById: (id: string) => request<any>(`/teachers/${id}`),
    getCourses: (id: string) => request<any[]>(`/teachers/${id}/courses`),
    getVideos: (id: string) => request<any[]>(`/teachers/${id}/videos`),
    getPosts: (id: string) => request<any[]>(`/teachers/${id}/posts`),
    getActivity: (id: string) => request<any>(`/teachers/${id}/activity`),
  },

  // ── Posts ──────────────────────────────────────
  posts: {
    getFeed: (page = 1) =>
      request<{ data: any[]; pagination: any }>(`/posts?page=${page}`),
    getById: (id: string) => request<any>(`/posts/${id}`),
    create: (data: { content: string; imageUrl?: string }) =>
      request<any>("/posts", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: { content?: string; imageUrl?: string }) =>
      request<any>(`/posts/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (id: string) => request<any>(`/posts/${id}`, { method: "DELETE" }),
    toggleLike: (id: string) =>
      request<any>(`/posts/${id}/like`, { method: "POST" }),
    addComment: (id: string, text: string) =>
      request<any>(`/posts/${id}/comment`, {
        method: "POST",
        body: JSON.stringify({ text }),
      }),
    toggleRepost: (id: string) =>
      request<any>(`/posts/${id}/repost`, { method: "POST" }),
    toggleBookmark: (id: string) =>
      request<any>(`/posts/${id}/bookmark`, { method: "POST" }),
  },

  // ── Payments ───────────────────────────────────
  payments: {
    initiate: (data: any) =>
      request<any>("/payments/initiate", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    verify: (paymentId: string) =>
      request<any>("/payments/verify", {
        method: "POST",
        body: JSON.stringify({ paymentId }),
      }),
    history: () => request<any[]>("/payments/history"),
  },

  // ── Premium ────────────────────────────────────
  premium: {
    getPlans: () => request<any[]>("/premium/plans"),
    subscribe: (plan: string) =>
      request<any>("/premium/subscribe", {
        method: "POST",
        body: JSON.stringify({ plan }),
      }),
    getStatus: () => request<any>("/premium/status"),
  },

  // ── Feedback ────────────────────────────────────
  feedback: {
    submit: (data: { name: string; email: string; message: string }) =>
      request<{ message: string }>("/feedback", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },

  // ── Categories ────────────────────────────────
  categories: {
    get: () =>
      request<{ courseCategories: string[]; videoCategories: string[] }>(
        "/categories",
      ),
  },

  // ── Search ─────────────────────────────────────
  search: {
    query: (q: string) =>
      request<{ courses: any[]; videos: any[]; teachers: any[] }>(
        `/search?q=${encodeURIComponent(q)}`,
      ),
  },

  // ── Upload ────────────────────────────────────
  upload: {
    avatar: async (file: File): Promise<{ url: string }> => {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch(`${API_BASE}/upload/avatar`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(res.status, body.message || "Upload failed");
      }
      return res.json();
    },
    image: async (file: File): Promise<{ url: string }> => {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("image", file);
      const res = await fetch(`${API_BASE}/upload/post-image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new ApiError(res.status, body.message || "Upload failed");
      }
      return res.json();
    },
  },
};
